/**
 * Which yards have a rabbit, and where it is allowed to be.
 *
 * Coordinates were traced against the shipped master plates plus the resolved
 * prop footprints, not estimated: every roam polygon sits inside the painted
 * fence and above the patio, and every cover point is a spot with real
 * scenery to disappear into.
 *
 * Yards absent from this map simply have no rabbit (Spanish Fork today).
 */

import type { RabbitYardConfig } from '@/world-prototype/creatures/rabbitBehavior';

/**
 * Why these polygons stop well short of the fence:
 *
 * Fences are painted into the master plates with no occlusion crop, so anything
 * the engine positions draws *over* them. Worse, the side fences recede
 * diagonally, which means the fence drawn level with a rabbit's ears sits
 * further into the yard than the fence at its feet. Clearing the fence
 * therefore takes a margin of roughly (rabbit height x fence slope) plus half
 * the rabbit's width — not a token inset.
 *
 * The player never hits this because her walk polygon is already inset. Rather
 * than add occlusion machinery for one small creature, the rabbit simply keeps
 * to the yard's interior; there is plenty of it, and the cover points still sit
 * beside real scenery.
 */

/**
 * Hubert — Grandpa's yard, 6 ft fence (a tall occlusion band). Cut off above
 * the concrete patio and clothesline in the foreground.
 */
const HUBERT_RABBIT: RabbitYardConfig = {
  yardId: 'hubert',
  roamPolygon: [
    { x: 0.42, y: 0.46 },
    { x: 0.7, y: 0.46 },
    { x: 0.8, y: 0.56 },
    { x: 0.88, y: 0.66 },
    { x: 0.92, y: 0.75 },
    { x: 0.92, y: 0.82 },
    { x: 0.11, y: 0.82 },
    { x: 0.11, y: 0.72 },
    { x: 0.19, y: 0.64 },
    { x: 0.3, y: 0.54 },
  ],
  cover: [
    { id: 'left-tree-base', x: 0.46, y: 0.475 },
    { id: 'right-tree-base', x: 0.6, y: 0.475 },
    { id: 'little-pine', x: 0.33, y: 0.545 },
    { id: 'garden-bed', x: 0.26, y: 0.72 },
    { id: 'past-shed', x: 0.9, y: 0.78 },
  ],
};

/**
 * Holly Springs — open lawn, 3 ft fence. The player's walk bounds here are a
 * loose rectangle that reaches up into the treeline, so the rabbit gets its own
 * interior polygon rather than reusing them.
 */
const HOLLY_SPRINGS_RABBIT: RabbitYardConfig = {
  yardId: 'raleigh',
  roamPolygon: [
    { x: 0.33, y: 0.45 },
    { x: 0.7, y: 0.45 },
    { x: 0.86, y: 0.6 },
    { x: 0.86, y: 0.79 },
    { x: 0.24, y: 0.79 },
    { x: 0.24, y: 0.6 },
  ],
  cover: [
    { id: 'little-pine', x: 0.685, y: 0.465 },
    { id: 'rear-lawn-edge', x: 0.35, y: 0.465 },
    { id: 'big-tree-base', x: 0.26, y: 0.615 },
    { id: 'swingset', x: 0.83, y: 0.66 },
  ],
};

const RABBIT_YARDS: Readonly<Record<string, RabbitYardConfig>> = {
  hubert: HUBERT_RABBIT,
  raleigh: HOLLY_SPRINGS_RABBIT,
};

/** Rabbit config for a yard, or null when that yard has no rabbit. */
export function rabbitConfigForYard(
  yardId: string | null | undefined,
): RabbitYardConfig | null {
  if (!yardId) return null;
  return RABBIT_YARDS[yardId] ?? null;
}

/** Yard ids that currently host a rabbit. */
export function rabbitYardIds(): readonly string[] {
  return Object.keys(RABBIT_YARDS);
}
